import type { Map as MapboxMap } from "mapbox-gl";
import type { AddressesResponse, AddressFeatureProperties, GeoJsonFeature } from "../../api/map/types";
import type { FlyerType } from "../components";
import { applyIncrementalUpdate, setCachedFeatures, getCachedFeatures } from "./utils";

// Telemetry callback type - optional to avoid circular dependencies
type TelemetryCallback = (eventName: string, payload?: Record<string, unknown>) => void;
let telemetryCallback: TelemetryCallback | null = null;

/**
 * Sets the telemetry callback for render timing
 */
export const setAddressesLayerTelemetry = (callback: TelemetryCallback | null): void => {
    telemetryCallback = callback;
    // Use callback variable to avoid unused variable warning
    void telemetryCallback;
};

export interface AddressesLayerOptions {
    map: MapboxMap;
    sourceId?: string;
    selectedFlyerTypeId?: string | null;
    selectedAddressIds?: string[];
}

const SOURCE_ID = "addresses";

/**
 * Computes address status based on flyer statuses
 * Returns: "delivered" | "partial" | "pending" | "blocked" | "hidden"
 */
export const getAddressStatus = (
    properties: AddressFeatureProperties,
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[],
    allCampaignsMode?: boolean
): string => {
    if (properties.isBlocked) {
        return "blocked";
    }
    if (properties.isHidden) {
        return "hidden";
    }

    if (!properties.flyerStatuses || properties.flyerStatuses.length === 0) {
        return "pending";
    }

    if (allCampaignsMode) {
        return properties.flyerStatuses.some((fs) => fs.deliveredQuantity > 0)
            ? "delivered"
            : "pending";
    }

    // If a specific flyer type is selected, check only that one
    // selectedFlyerTypeId can be either FlyerType.id or CampaignFlyer.id
    if (selectedFlyerTypeId) {
        const flyerStatus = properties.flyerStatuses.find(
            (fs) => fs.flyerTypeId === selectedFlyerTypeId || fs.campaignFlyerId === selectedFlyerTypeId
        );
        if (!flyerStatus) {
            return "pending";
        }
        return flyerStatus.deliveredQuantity > 0 ? "delivered" : "pending";
    }

    // If campaign flyer types are provided, filter to only those
    // This ensures we only check flyer types that belong to the current campaign
    if (campaignFlyerTypes && campaignFlyerTypes.length > 0) {
        const campaignFlyerTypeIds = new Set(campaignFlyerTypes.map(ft => ft.id));

        // Filter to only statuses for flyer types in this campaign
        // Match by either flyerTypeId or campaignFlyerId
        // For campaign-specific flyers: campaignFlyerId should match CampaignFlyer.id (which is in campaignFlyerTypeIds)
        const relevantStatuses = properties.flyerStatuses.filter(
            fs => {
                // Match by FlyerTypeId if it exists
                if (fs.flyerTypeId && campaignFlyerTypeIds.has(fs.flyerTypeId)) {
                    return true;
                }
                // Match by CampaignFlyerId if it exists (for campaign-specific flyers)
                if (fs.campaignFlyerId && campaignFlyerTypeIds.has(fs.campaignFlyerId)) {
                    return true;
                }
                return false;
            }
        );

        if (relevantStatuses.length === 0) {
            return "pending";
        }

        const deliveredCount = relevantStatuses.filter(
            fs => fs.deliveredQuantity > 0
        ).length;
        const totalCount = relevantStatuses.length;

        if (deliveredCount === totalCount) {
            return "delivered";
        }
        if (deliveredCount > 0) {
            return "partial";
        }
        return "pending";
    }

    // Fallback: Multi-flyer logic (if campaignFlyerTypes not provided)
    const deliveredCount = properties.flyerStatuses.filter(
        (fs) => fs.deliveredQuantity > 0
    ).length;
    const totalCount = properties.flyerStatuses.length;

    if (deliveredCount === totalCount && totalCount > 0) {
        return "delivered";
    }
    if (deliveredCount > 0) {
        return "partial";
    }
    return "pending";
};

/**
 * Ensures the addresses layer exists on the map (idempotent)
 */
export const ensureAddressesLayer = ({
    map,
    sourceId = SOURCE_ID
}: AddressesLayerOptions): void => {
    // Try to create source - if style is not ready, this will fail silently
    // The updateAddressesLayer function handles waiting for style.load
    try {
        if (!map.getSource(sourceId)) {
            map.addSource(sourceId, {
                type: "geojson",
                data: { type: "FeatureCollection", features: [] }
            });
        }
    } catch (error) {
        // If source creation fails (style not loaded), just return
        // The caller (updateAddressesLayer) will handle waiting for style.load
        return;
    }

    // Remove existing layer if it exists (to update styling)
    const layerId = `${sourceId}-points`;
    if (map.getLayer(layerId)) {
        map.removeLayer(layerId);
    }
    try {
    map.addLayer({
        id: layerId,
        type: "circle",
        source: sourceId,
        layout: {
            visibility: "visible" // Explicitly set visibility
        },
        paint: {
            // Base radius from zoom, scaled up for high deliveryUnitCount (many lador per point).
            // scale = clamp(1..2.3, 1 + 0.12 * (sqrt(max(1, n)) - 1)); ~2.1x at n=100.
            "circle-radius": [
                // zoom must be the direct input to the top-level interpolate
                // deliveryUnitCount scaling is applied per-stop as a feature expression
                "interpolate",
                ["linear"],
                ["zoom"],
                10, ["*", 4,  ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]],
                12, ["*", 6,  ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]],
                15, ["*", 9,  ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]],
                18, ["*", 12, ["max", 1, ["min", 2.3, ["+", 1, ["*", 0.12, ["-", ["sqrt", ["max", 1, ["coalesce", ["get", "deliveryUnitCount"], 1]]], 1]]]]]]
            ],
            "circle-color": [
                "case",
                ["get", "isSelected"],
                "#8b5cf6", // purple for selected addresses
                [
                    "match",
                    // Feature-state "overrideStatus" takes precedence for immediate optimistic updates
                    ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
                    "delivered",
                    "#114838", // dark green (matches iOS)
                    "partial",
                    "#F59E1A", // orange/amber (matches iOS)
                    "pending",
                    "#F6C85F", // yellow/amber (matches iOS)
                    "blocked",
                    "#D92626", // red (matches iOS)
                    "hidden",
                    "#858585", // gray (matches iOS)
                    "#F6C85F" // default: pending color
                ]
            ],
            "circle-stroke-color": [
                "case",
                ["get", "isSelected"],
                "#ffffff", // white stroke for selected
                "#ffffff"
            ],
            "circle-stroke-width": [
                "case",
                ["get", "isSelected"],
                3, // thicker stroke for selected
                1
            ],
            "circle-opacity": [
                "match",
                ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
                "hidden",
                0.3,
                1
            ]
        }
    });
    } catch (e) {
        console.error("[AddressesLayer] addLayer failed — paint expression invalid:", e);
    }
};

// Track maps where the paint expression has been updated to support feature-state overrides
const paintExpressionUpdated = new WeakSet<MapboxMap>();

/**
 * Sets an immediate delivery status override on a single address using Mapbox feature-state.
 * This is independent of the data pipeline (setData/React Query), so it takes effect on the
 * next frame and persists through subsequent updateAddressesLayer calls.
 * Use null to clear the override.
 */
export const setAddressDeliveryOverride = (
    map: MapboxMap,
    addressId: string,
    status: "delivered" | "pending" | null
): void => {
    const layerId = `${SOURCE_ID}-points`;

    // Ensure the paint expression uses ["feature-state", "overrideStatus"] — do this only once per map.
    // New layers created by ensureAddressesLayer already include it; this updates any pre-existing layer.
    if (!paintExpressionUpdated.has(map) && map.getLayer(layerId)) {
        try {
            map.setPaintProperty(layerId, "circle-color", [
                "case",
                ["get", "isSelected"],
                "#8b5cf6",
                [
                    "match",
                    ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
                    "delivered", "#22c55e",
                    "partial", "#facc15",
                    "pending", "#3b82f6",
                    "blocked", "#ef4444",
                    "hidden", "#94a3b8",
                    "#3b82f6"
                ]
            ]);
            map.setPaintProperty(layerId, "circle-opacity", [
                "match",
                ["coalesce", ["feature-state", "overrideStatus"], ["get", "status"]],
                "hidden", 0.3,
                1
            ]);
            paintExpressionUpdated.add(map);
        } catch (e) {
            console.warn("[AddressesLayer] Could not update paint property for feature-state override:", e);
        }
    }

    if (status === null) {
        try {
            map.removeFeatureState({ source: SOURCE_ID, id: addressId }, "overrideStatus");
        } catch (e) {
            // Source may not exist yet
        }
    } else {
        try {
            map.setFeatureState({ source: SOURCE_ID, id: addressId }, { overrideStatus: status });
        } catch (e) {
            console.warn("[AddressesLayer] Could not set feature-state override:", e);
        }
    }
};

// Track if we're waiting for style.load to avoid registering multiple listeners
let pendingStyleLoad = false;

/**
 * Updates the addresses layer with data from the API
 * Uses incremental updates when possible to minimize re-rendering
 */
export const updateAddressesLayer = (
    map: MapboxMap,
    data: AddressesResponse | null,
    selectedFlyerTypeId?: string | null,
    useIncremental: boolean = true,
    isDrawingPolygon: boolean = false,
    selectedAddressIds?: string[],
    campaignFlyerTypes?: FlyerType[],
    allCampaignsMode?: boolean
): void => {
    // Check if source and layer exist - if they do, we can update even if isStyleLoaded() returns false
    // (isStyleLoaded() can return false even after style.load event has fired)
    let source = map.getSource(SOURCE_ID);
    const layerId = `${SOURCE_ID}-points`;
    let layer = map.getLayer(layerId);

    // If source and layer don't exist AND style is not loaded, wait for style.load
    if ((!source || !layer) && !map.isStyleLoaded()) {
        // Only register ONE style.load listener if we haven't already
        if (!pendingStyleLoad) {
            pendingStyleLoad = true;
            ensureAddressesLayer({ map, sourceId: SOURCE_ID, selectedFlyerTypeId });
            map.once("style.load", () => {
                pendingStyleLoad = false;
                updateAddressesLayer(map, data, selectedFlyerTypeId, useIncremental, isDrawingPolygon);
            });
        }
        return;
    }

    // Reset pendingStyleLoad flag if source and layer exist (style is actually ready)
    if (source && layer && pendingStyleLoad) {
        pendingStyleLoad = false;
    }

    if (!source || source.type !== "geojson" || !layer) {
        console.warn(`[AddressesLayer] Source or layer missing. Source: ${source ? 'exists' : 'missing'}, Layer: ${layer ? 'exists' : 'missing'}. Creating...`);
        ensureAddressesLayer({ map, sourceId: SOURCE_ID, selectedFlyerTypeId });

        // Get source and layer again after ensuring they exist
        source = map.getSource(SOURCE_ID);
        layer = map.getLayer(layerId);

        if (!source || source.type !== "geojson") {
            console.error(`[AddressesLayer] Failed to create source`);
            return;
        }

        if (!layer) {
            console.error(`[AddressesLayer] Failed to create layer`);
            return;
        }
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Don't clear data if it's null/undefined - keep existing features visible
    // This prevents addresses from disappearing during query refetch
    if (!data) {
        return;
    }

    if (data.features.length === 0) {
        // Only clear if we explicitly get empty data (not just null/undefined)
        geojsonSource.setData({ type: "FeatureCollection", features: [] } as any);
        return;
    }

    // All-campaigns overview: only show delivered, non-blocked, non-hidden points
    const sourceFeatures = allCampaignsMode
        ? data.features.filter((f) => {
            const p = f.properties;
            if (p.isBlocked || p.isHidden) {
                return false;
            }
            return (p.flyerStatuses ?? []).some((fs) => fs.deliveredQuantity > 0);
        })
        : data.features;

    // Check if features have geometry
    const featuresWithGeometry = sourceFeatures.filter(f => f.geometry && f.geometry.type === "Point");

    if (featuresWithGeometry.length === 0) {
        console.warn(`[AddressesLayer] No features with Point geometry!`);
        return;
    }

    // Convert API response to Mapbox GeoJSON format with computed status
    const newFeatures: GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>[] = sourceFeatures.map((feature) => {
        const addressId = feature.id ?? feature.properties.addressPointId;
        const isSelected = selectedAddressIds ? selectedAddressIds.includes(String(addressId)) : false;

        return {
            type: "Feature" as const,
            id: addressId,
            geometry: feature.geometry,
            properties: {
                ...feature.properties,
                status: getAddressStatus(feature.properties, selectedFlyerTypeId, campaignFlyerTypes, allCampaignsMode),
                // Add helper properties for filtering/styling
                addressPointId: feature.properties.addressPointId,
                deliveredCount: feature.properties.flyerStatuses.filter(
                    (fs) => fs.deliveredQuantity > 0
                ).length,
                totalFlyerCount: feature.properties.flyerStatuses.length,
                isSelected
            }
        };
    });

    // Use incremental update if enabled (but not when flyer filter changes, as status changes)
    // For addresses, use keepExistingFeatures=true to prevent addresses from disappearing when zooming out
    // BUT: When we have merged data from multiple tiles, always do full update to ensure all tiles are included
    // DISABLED: Incremental updates can miss features when viewport changes during panning
    // Always use full update for addresses to ensure all features are visible when panning
    const isMergedData = data.features.length > 500; // Heuristic: merged data usually has more features
    if (false && useIncremental && selectedFlyerTypeId === undefined && !isMergedData) {
        try {
            applyIncrementalUpdate(
                map,
                geojsonSource,
                SOURCE_ID,
                newFeatures,
                (feature) => feature.id ?? feature.properties.addressPointId,
                true // keepExistingFeatures: keep all addresses, only add new ones
            );

            // Update layer styling if needed
            if (map.getLayer(`${SOURCE_ID}-points`)) {
                ensureAddressesLayer({
                    map,
                    sourceId: SOURCE_ID,
                    selectedFlyerTypeId,
                    selectedAddressIds
                });
            }
            return;
        } catch (error) {
            // Fallback to full update on error
            console.warn("Incremental update failed, falling back to full update:", error);
        }
    }

    // Full update (incremental disabled, filter changed, or error)
    // For addresses, merge with existing features to prevent disappearing when zooming out
    const currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);
    const mergedFeatures = new Map<string | number, GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>>();

    // Add all existing features, updating isSelected if needed
    currentFeatures.forEach((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;
        if (id !== undefined) {
            const isSelected = selectedAddressIds ? selectedAddressIds.includes(String(id)) : false;
            mergedFeatures.set(id, {
                ...feature,
                properties: {
                    ...feature.properties,
                    isSelected
                }
            });
        }
    });

    // Add/update with new features (will overwrite existing if same ID)
    // Trust flyerStatuses from the incoming combined addresses response (static + deliveries merge in hooks).
    // Previously we merged with cached statuses when new had fewer/empty rows; that kept addresses green after
    // "ångra utdelning" when refetch correctly returned no deliveries for that address.
    newFeatures.forEach((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;
        if (id !== undefined) {
            mergedFeatures.set(id, feature);
        }
    });

    // Update isSelected for existing features that weren't in newFeatures
    mergedFeatures.forEach((feature, id) => {
        if (!newFeatures.find(f => (f.id ?? f.properties.addressPointId) === id)) {
            const isSelected = selectedAddressIds ? selectedAddressIds.includes(String(id)) : false;
            mergedFeatures.set(id, {
                ...feature,
                properties: {
                    ...feature.properties,
                    isSelected
                }
            });
        }
    });

    const mergedArray = Array.from(mergedFeatures.values());
    const geojson = {
        type: "FeatureCollection" as const,
        features: mergedArray
    };

    // Set data and cache features
    geojsonSource.setData(geojson as any);
    setCachedFeatures(map, SOURCE_ID, mergedArray);

    // Ensure layer exists and is styled correctly
    if (!map.getLayer(layerId)) {
        console.warn(`[AddressesLayer] Layer ${layerId} missing after setData, recreating...`);
        ensureAddressesLayer({
            map,
            sourceId: SOURCE_ID,
            selectedFlyerTypeId
        });
    } else {
        // Set layer visibility based on polygon drawing state
        const targetVisibility = isDrawingPolygon ? "none" : "visible";
        const layerVisibility = map.getLayoutProperty(layerId, "visibility");
        if (layerVisibility !== targetVisibility) {
            map.setLayoutProperty(layerId, "visibility", targetVisibility);
        }
    }

    // setData() schedules Mapbox's own render pipeline — no manual repaint needed.
};

/**
 * Updates a single address point's position on the map immediately
 * This provides instant visual feedback before the API response
 */
export const updateAddressPosition = (
    map: MapboxMap,
    addressId: string | number,
    newLat: number,
    newLon: number
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = (geojsonSource as any)._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            console.warn("[AddressesLayer] Could not access source data for position update:", error);
            return;
        }
    }

    const featureToUpdate = currentFeatures.find(
        (f) => {
            const id = f.id ?? f.properties.addressPointId;
            return id === addressId || String(id) === String(addressId);
        }
    );

    if (!featureToUpdate) {
        console.warn(`[AddressesLayer] Address ${addressId} not found for position update`);
        return;
    }

    const updatedFeature: GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }> = {
        ...featureToUpdate,
        geometry: {
            type: "Point",
            coordinates: [newLon, newLat]
        }
    };

    const updatedFeatures = currentFeatures.map((f) => {
        const id = f.id ?? f.properties.addressPointId;
        if (id === addressId || String(id) === String(addressId)) {
            return updatedFeature;
        }
        return f;
    });

    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Updates a single address point's status (blocked/hidden) on the map immediately
 * This provides instant visual feedback before the API response
 */
export const updateAddressStatus = (
    map: MapboxMap,
    addressId: string | number,
    isBlocked?: boolean,
    isHidden?: boolean,
    selectedFlyerTypeId?: string | null
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = geojsonSource._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            // If we can't access source data, just return
            console.warn("[AddressesLayer] Could not access source data for status update:", error);
            return;
        }
    }

    // Find the feature to update
    const featureToUpdate = currentFeatures.find(
        (f) => {
            const id = f.id ?? f.properties.addressPointId;
            return id === addressId || String(id) === String(addressId);
        }
    );

    if (!featureToUpdate) {
        // Feature not found - might not be loaded yet
        console.warn(`[AddressesLayer] Address ${addressId} not found in current features for status update`);
        return;
    }

    // Update the feature's properties
    const updatedProperties = {
        ...featureToUpdate.properties,
        isBlocked: isBlocked !== undefined ? isBlocked : featureToUpdate.properties.isBlocked,
        isHidden: isHidden !== undefined ? isHidden : featureToUpdate.properties.isHidden
    };

    // Recalculate status based on updated properties
    const updatedStatus = getAddressStatus(updatedProperties, selectedFlyerTypeId);

    const updatedFeature: GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }> = {
        ...featureToUpdate,
        properties: {
            ...updatedProperties,
            status: updatedStatus
        }
    };

    // Update all features: replace the updated one, keep others
    const updatedFeatures = currentFeatures.map((f) => {
        const id = f.id ?? f.properties.addressPointId;
        if (id === addressId || String(id) === String(addressId)) {
            return updatedFeature;
        }
        return f;
    });

    // Update the source
    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    // Update cache
    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Optimistically marks addresses as delivered immediately after delivery is created.
 * Updates the flyerStatuses to include the new deliveries and recalculates status.
 * 
 * @param map - The Mapbox map instance
 * @param addressIds - Array of address IDs to mark as delivered
 * @param flyerTypeIds - Array of flyer type IDs that were delivered
 * @param selectedFlyerTypeId - Optional selected flyer type ID for status calculation
 * @param campaignFlyerTypes - Optional campaign flyer types for status calculation
 */
export const markAddressesAsDelivered = (
    map: MapboxMap,
    addressIds: string[],
    flyerTypeIds: string[],
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[]
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;
    const addressIdSet = new Set(addressIds.map(id => String(id)));

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = geojsonSource._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            console.warn("[AddressesLayer] Could not access source data for marking as delivered:", error);
            return;
        }
    }

    // Update features that match the address IDs
    let updatedCount = 0;
    const updatedFeatures = currentFeatures.map((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;
        const addressIdStr = String(id);
        
        if (!addressIdSet.has(addressIdStr)) {
            return feature; // Not in the list of addresses to update
        }

        updatedCount++;

        // Get existing flyerStatuses or create empty array
        const existingFlyerStatuses = feature.properties.flyerStatuses || [];
        const flyerStatusMap = new Map(existingFlyerStatuses.map(fs => [fs.flyerTypeId, fs]));

        // Update or add flyerStatus for each delivered flyerTypeId
        for (const flyerTypeId of flyerTypeIds) {
            const existingStatus = flyerStatusMap.get(flyerTypeId);
            if (existingStatus) {
                // Update existing status - ensure deliveredQuantity > 0
                flyerStatusMap.set(flyerTypeId, {
                    ...existingStatus,
                    deliveredQuantity: Math.max(existingStatus.deliveredQuantity, 1),
                    lastDeliveredAt: new Date().toISOString()
                });
            } else {
                // Add new status for this flyer type
                flyerStatusMap.set(flyerTypeId, {
                    addressPointId: feature.properties.addressPointId,
                    campaignId: "", // Will be updated on next refetch
                    flyerTypeId: flyerTypeId,
                    deliveredQuantity: 1,
                    lastDeliveredAt: new Date().toISOString(),
                    recordedById: "" // Will be updated on next refetch
                });
            }
        }

        // Convert map back to array
        const updatedFlyerStatuses = Array.from(flyerStatusMap.values());

        // Update properties with new flyerStatuses
        const updatedProperties = {
            ...feature.properties,
            flyerStatuses: updatedFlyerStatuses
        };

        // Recalculate status based on updated flyerStatuses
        const updatedStatus = getAddressStatus(updatedProperties, selectedFlyerTypeId, campaignFlyerTypes);

        // Calculate deliveredCount and totalFlyerCount
        const deliveredCount = updatedFlyerStatuses.filter(fs => fs.deliveredQuantity > 0).length;
        const totalFlyerCount = updatedFlyerStatuses.length;

        return {
            ...feature,
            properties: {
                ...updatedProperties,
                status: updatedStatus,
                deliveredCount,
                totalFlyerCount
            }
        };
    });

    if (updatedCount === 0) {
        // No addresses found - might not be loaded yet
        console.warn(`[AddressesLayer] No addresses found to mark as delivered (IDs: ${addressIds.join(", ")})`);
        return;
    }

    // Update the source
    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    // Update cache
    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};

/**
 * Optimistically marks an address as undelivered (pending) for a specific flyer type.
 * Removes the delivery from flyerStatuses and recalculates status.
 *
 * @param map - The Mapbox map instance
 * @param addressId - The address ID to mark as undelivered
 * @param flyerTypeId - The flyer type ID to remove delivery for
 * @param selectedFlyerTypeId - Optional selected flyer type ID for status calculation
 * @param campaignFlyerTypes - Optional campaign flyer types for status calculation
 */
export const markAddressAsUndelivered = (
    map: MapboxMap,
    addressId: string | number,
    flyerTypeId: string,
    selectedFlyerTypeId?: string | null,
    campaignFlyerTypes?: FlyerType[]
): void => {
    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== "geojson") {
        console.warn("[AddressesLayer] markAddressAsUndelivered: Source not found");
        return;
    }

    const geojsonSource = source as mapboxgl.GeoJSONSource;

    // Get current data from cache first, fallback to source data
    let currentFeatures = getCachedFeatures<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>(map, SOURCE_ID);

    // If cache is empty, try to get from source directly
    if (currentFeatures.length === 0) {
        try {
            const sourceData = (geojsonSource as any)._data as { type: "FeatureCollection"; features: Array<GeoJsonFeature<AddressFeatureProperties & { status: string; deliveredCount: number; totalFlyerCount: number; isSelected: boolean }>> } | null;
            if (sourceData && sourceData.features) {
                currentFeatures = sourceData.features;
            }
        } catch (error) {
            console.warn("[AddressesLayer] Could not access source data for marking as undelivered:", error);
            return;
        }
    }

    // Find and update the feature
    let found = false;
    const updatedFeatures = currentFeatures.map((feature) => {
        const id = feature.id ?? feature.properties.addressPointId;

        if (String(id) !== String(addressId)) {
            return feature; // Not the address we're looking for
        }

        found = true;

        // Remove flyerStatus for the specified flyerTypeId
        const existingFlyerStatuses = feature.properties.flyerStatuses || [];
        const updatedFlyerStatuses = existingFlyerStatuses.filter(
            (fs) => fs.flyerTypeId !== flyerTypeId && fs.campaignFlyerId !== flyerTypeId
        );

        // Update properties with filtered flyerStatuses
        const updatedProperties = {
            ...feature.properties,
            flyerStatuses: updatedFlyerStatuses
        };

        // Recalculate status based on updated flyerStatuses
        const updatedStatus = getAddressStatus(updatedProperties, selectedFlyerTypeId, campaignFlyerTypes);

        // Calculate deliveredCount and totalFlyerCount
        const deliveredCount = updatedFlyerStatuses.filter(fs => fs.deliveredQuantity > 0).length;
        const totalFlyerCount = updatedFlyerStatuses.length;

        return {
            ...feature,
            properties: {
                ...updatedProperties,
                status: updatedStatus,
                deliveredCount,
                totalFlyerCount
            }
        };
    });

    if (!found) {
        console.warn(`[AddressesLayer] Address ${addressId} not found for marking as undelivered`);
        return;
    }

    // Update the source
    geojsonSource.setData({
        type: "FeatureCollection",
        features: updatedFeatures
    } as any);

    // Update cache
    setCachedFeatures(map, SOURCE_ID, updatedFeatures);
};